import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Stack,
  Divider,
  IconButton,
  Tooltip,
  Button
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Edit as EditIcon,
  Work as WorkIcon,
  Code as CodeIcon,
  Payments as PaymentsIcon,
  LocationOn as LocationOnIcon,
  Business as BusinessIcon,
  HealthAndSafety as HealthAndSafetyIcon,
  Title as TitleIcon,
  Description as DescriptionIcon,
  Assignment as AssignmentIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { accentBorderCard, designTokens } from '../../theme/designTokens';
import { text as textColors } from '../../utils/darkModeAdapter';

const theme = designTokens.featureThemes.job_posting;

const StyledCard = styled(Card)(({ theme: muiTheme }) => ({
  width: '100%',
  ...accentBorderCard('job_posting', muiTheme),
  paddingLeft: muiTheme.spacing(1),
  '&:hover': {
    transform: 'translateY(-2px)',
  },
}));

const CardTitle = styled(Typography)(({ theme: muiTheme }) => ({
  fontWeight: 600,
  marginBottom: muiTheme.spacing(2),
  color: theme.primary,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
}));

const RequirementItem = styled(Box)(({ theme }) => ({
  margin: theme.spacing(1.5, 0),
}));

const RequirementLabel = styled(Typography)(({ theme: muiTheme }) => ({
  fontWeight: 600,
  fontSize: '0.9rem',
  color: textColors(muiTheme).secondary,
  marginBottom: muiTheme.spacing(0.5),
  display: 'flex',
  alignItems: 'center',
  '& svg': {
    marginRight: muiTheme.spacing(1),
    color: theme.primary
  }
}));

const RequirementContent = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  paddingLeft: '28px'
}));

const EditButton = styled(Button)(({ theme }) => ({
  borderRadius: '20px',
  padding: theme.spacing(0.5, 2),
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.85rem',
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  whiteSpace: 'nowrap'
}));

const RequirementsCard = ({ requirements, onEdit }) => {
  const shouldRenderField = (field) => {
    if (!requirements) return false;
    
    const value = requirements[field];
    if (value === undefined || value === null) return false;
    
    if (typeof value === 'string') {
      return value.trim() !== '';
    }
    
    return true;
  };

  // 給与を万円単位で表示する関数
  const formatSalary = (salary) => {
    if (!salary) return '';
    
    // 数値の場合は文字列に変換
    const salaryStr = typeof salary === 'string' ? salary : String(salary);
    
    // すでに「万円」で終わっている場合はそのまま返す
    if (salaryStr.trim().endsWith('万円')) {
      return salaryStr;
    }
    
    // すでに「円」で終わっている場合はそのまま返す（万円以外の単位の場合）
    if (salaryStr.trim().endsWith('円') && !salaryStr.includes('万')) {
      return salaryStr;
    }
    
    // 範囲表示の場合（例：300000-400000）
    if (salaryStr.includes('-') || salaryStr.includes('〜') || salaryStr.includes('～')) {
      const separator = salaryStr.includes('-') ? '-' : (salaryStr.includes('〜') ? '〜' : '～');
      const parts = salaryStr.split(separator);
      
      if (parts.length === 2) {
        const formatPart = (part) => {
          const num = parseInt(part.trim().replace(/[^\d]/g, ''));
          if (!isNaN(num) && num >= 10000) {
            return `${Math.floor(num / 10000)}万円`;
          }
          return part.trim();
        };
        
        return `${formatPart(parts[0])}${separator}${formatPart(parts[1])}`;
      }
    }
    
    // 単一の数値の場合
    const numMatch = salaryStr.match(/\d+/);
    if (numMatch) {
      const num = parseInt(numMatch[0]);
      if (!isNaN(num) && num >= 10000) {
        // 1万円以上の場合は万円単位に変換
        const manEn = Math.floor(num / 10000);
        const remainder = num % 10000;
        
        if (remainder === 0) {
          return `${manEn}万円`;
        } else {
          // 端数がある場合は小数点表示
          return `${(num / 10000).toFixed(1)}万円`;
        }
      } else {
        // 1万円未満の場合はそのまま円表示
        return `${num}円`;
      }
    }
    
    // 数値が見つからない場合はそのまま返す
    return salaryStr.endsWith('円') ? salaryStr : `${salaryStr}円`;
  };

  return (
    <StyledCard>
      <CardContent>
        <CardTitle variant="h6">
          <Box sx={{ display: 'flex', alignItems: 'center' }}>求人要件</Box>
          <EditButton
            variant="contained"
            color="primary"
            onClick={onEdit}
            startIcon={<EditIcon />}
            sx={{
              background: `linear-gradient(90deg, ${theme.primary}, ${theme.light})`,
              '&:hover': {
                background: `linear-gradient(90deg, #388E3C, #66BB6A)`
              }
            }}
          >
            内容を修正して再生成
          </EditButton>
        </CardTitle>
        
        <Divider sx={{ mb: 2 }} />

        {/* existingモード: タイトル・概要・仕事内容・求める人材 */}
        {requirements?.mode === 'existing' ? (
          <>
            {shouldRenderField('title') && (
              <RequirementItem>
                <RequirementLabel>
                  <TitleIcon fontSize="small" />
                  タイトル
                </RequirementLabel>
                <RequirementContent>{requirements.title}</RequirementContent>
              </RequirementItem>
            )}

            {shouldRenderField('summary') && (
              <RequirementItem>
                <RequirementLabel>
                  <DescriptionIcon fontSize="small" />
                  概要
                </RequirementLabel>
                <RequirementContent sx={{ whiteSpace: 'pre-wrap' }}>{requirements.summary}</RequirementContent>
              </RequirementItem>
            )}

            {shouldRenderField('jobDescription') && (
              <RequirementItem>
                <RequirementLabel>
                  <AssignmentIcon fontSize="small" />
                  仕事内容
                </RequirementLabel>
                <RequirementContent sx={{ whiteSpace: 'pre-wrap' }}>{requirements.jobDescription}</RequirementContent>
              </RequirementItem>
            )}

            {shouldRenderField('targetPerson') && (
              <RequirementItem>
                <RequirementLabel>
                  <PersonIcon fontSize="small" />
                  求める人材
                </RequirementLabel>
                <RequirementContent sx={{ whiteSpace: 'pre-wrap' }}>{requirements.targetPerson}</RequirementContent>
              </RequirementItem>
            )}

            {shouldRenderField('salary') && (
              <RequirementItem>
                <RequirementLabel>
                  <PaymentsIcon fontSize="small" />
                  給与
                </RequirementLabel>
                <RequirementContent>{formatSalary(requirements.salary)}</RequirementContent>
              </RequirementItem>
            )}
          </>
        ) : (
          <>
            {/* newモード: 募集職種・スキル・給与・勤務地・会社PR・福利厚生 */}
            {shouldRenderField('role') && (
              <RequirementItem>
                <RequirementLabel>
                  <WorkIcon fontSize="small" />
                  募集職種
                </RequirementLabel>
                <RequirementContent>{requirements.role}</RequirementContent>
              </RequirementItem>
            )}

            {shouldRenderField('skills') && (
              <RequirementItem>
                <RequirementLabel>
                  <CodeIcon fontSize="small" />
                  必要なスキル・経験
                </RequirementLabel>
                <Stack direction="row" spacing={1} flexWrap="wrap" gap={1} sx={{ paddingLeft: '28px' }}>
                  {typeof requirements.skills === 'string'
                    ? requirements.skills.split(',').map((skill, index) => (
                        <Chip
                          key={index}
                          label={skill.trim()}
                          size="small"
                          variant="outlined"
                          sx={{
                            borderRadius: '4px',
                            bgcolor: 'rgba(25, 118, 210, 0.04)',
                          }}
                        />
                      ))
                    : requirements.skills.map((skill, index) => (
                        <Chip
                          key={index}
                          label={skill}
                          size="small"
                          variant="outlined"
                          sx={{
                            borderRadius: '4px',
                            bgcolor: 'rgba(25, 118, 210, 0.04)',
                          }}
                        />
                      ))
                  }
                </Stack>
              </RequirementItem>
            )}

            {shouldRenderField('salary') && (
              <RequirementItem>
                <RequirementLabel>
                  <PaymentsIcon fontSize="small" />
                  給与
                </RequirementLabel>
                <RequirementContent>{formatSalary(requirements.salary)}</RequirementContent>
              </RequirementItem>
            )}

            {shouldRenderField('location') && (
              <RequirementItem>
                <RequirementLabel>
                  <LocationOnIcon fontSize="small" />
                  勤務地
                </RequirementLabel>
                <RequirementContent>{requirements.location}</RequirementContent>
              </RequirementItem>
            )}

            {shouldRenderField('companyPr') && (
              <RequirementItem>
                <RequirementLabel>
                  <BusinessIcon fontSize="small" />
                  会社PR
                </RequirementLabel>
                <RequirementContent>{requirements.companyPr}</RequirementContent>
              </RequirementItem>
            )}

            {shouldRenderField('welfare') && (
              <RequirementItem>
                <RequirementLabel>
                  <HealthAndSafetyIcon fontSize="small" />
                  福利厚生
                </RequirementLabel>
                <RequirementContent>{requirements.welfare}</RequirementContent>
              </RequirementItem>
            )}
          </>
        )}
      </CardContent>
    </StyledCard>
  );
};

export default RequirementsCard; 